// ============================================================================================
// LES FAMILLES DU JOURNAL DE LA CONSOLE, À L'ÉCRAN — le mot qu'un humain lit pour chaque famille.
//
// UTILITÉ : le serveur range chaque événement sous une famille (`console_event.family`) ; la valeur est une CLÉ
// enregistrée en base (« provisioning »), pas un texte. Le tableau de bord, le journal et l'historique d'un client
// affichent tous la même famille : ils la nomment depuis ce module, sans quoi « provisioning » s'affichait brut d'un côté
// et autrement de l'autre.
// ⚠️ LA CLÉ NE SE RENOMME PAS : elle est persistée dans chaque ligne du journal. Seul son libellé change ici.
// ============================================================================================
import type { ActivityEvent } from './api';

type Family = ActivityEvent['family'];

/** Le libellé de chaque famille, dans l'ordre où les filtres les proposent. */
export const FAMILY_LABEL: Record<Family, string> = {
  provisioning: 'mise en service',
  flotte: 'flotte',
  blocages: 'blocages',
  données: 'données',
  clients: 'clients',
  plateforme: 'plateforme',
};

/** Le libellé d'une famille lue en base — une famille inconnue (écrite par une version plus récente) se montre telle quelle. */
export function familyLabel(f: string): string {
  return (FAMILY_LABEL as Record<string, string>)[f] ?? f;
}

/**
 * La couleur de chaque famille — LA MÊME au journal, dans l'historique d'un client et sur la fiche magasin.
 * ⚠️ UNE TABLE : deux tables de couleurs pour les mêmes familles finiraient par diverger, et « blocages » ne serait plus
 *    rouge au même endroit selon l'écran.
 */
export const FAMILY_TONE: Record<string, { bg: string; fg: string }> = {
  provisioning: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' },
  flotte: { bg: 'var(--in-bg)', fg: 'var(--in-fg)' },
  blocages: { bg: 'var(--da-bg)', fg: 'var(--da-fg)' },
  données: { bg: 'var(--vi-bg)', fg: 'var(--vi-fg)' },
  clients: { bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  plateforme: { bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' },
};

/** La couleur d'une famille lue en base — une famille inconnue prend celle de la plateforme, jamais rien. */
export function familyTone(f: string): { bg: string; fg: string } {
  return FAMILY_TONE[f] ?? FAMILY_TONE.plateforme!;
}
